import { deriveInstallmentScheduleDates } from "../utils/installmentScheduleDates";

const INSTALLMENT_EXPENSE_MEMO_PREFIX = "할부 내역 자동 등록";

const toNumberOrNull = (value) => {
  const cleaned = String(value ?? "").replace(/[^\d.-]/g, "");
  if (!cleaned || cleaned === "-") return null;
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : null;
};

const scheduleOverrideFields = ["payment", "principal", "interest", "stampDuty", "balance"];

const normalizeScheduleOverrides = (value) => {
  if (!value || typeof value !== "object") return {};
  if (Array.isArray(value)) {
    return value.reduce((acc, item) => {
      const round = Number(item?.round);
      if (!Number.isFinite(round)) return acc;
      acc[round] = scheduleOverrideFields.reduce((fieldAcc, field) => {
        if (item[field] !== undefined && item[field] !== null && String(item[field]).trim() !== "") {
          fieldAcc[field] = item[field];
        }
        return fieldAcc;
      }, {});
      return acc;
    }, {});
  }

  return Object.entries(value).reduce((acc, [roundKey, fields]) => {
    const round = Number(roundKey);
    if (!Number.isFinite(round) || !fields || typeof fields !== "object") return acc;
    acc[round] = scheduleOverrideFields.reduce((fieldAcc, field) => {
      if (fields[field] !== undefined && fields[field] !== null && String(fields[field]).trim() !== "") {
        fieldAcc[field] = fields[field];
      }
      return fieldAcc;
    }, {});
    return acc;
  }, {});
};

const getScheduleOverrideValue = (row, round, field) => {
  const roundNumber = Number(round);
  if (!Number.isFinite(roundNumber)) return null;
  const overrides = normalizeScheduleOverrides(row.payment_schedule_overrides);
  const value = overrides?.[roundNumber]?.[field];
  return value === undefined || value === null || String(value).trim() === ""
    ? null
    : toNumberOrNull(value);
};

const toMonthDate = (month) => `${String(month || "").slice(0, 7)}-01`;

const getMonthIndex = (dateOrMonth) => {
  const text = String(dateOrMonth || "");
  const match = text.match(/^(\d{4})-(\d{2})/);
  if (!match) return null;
  return Number(match[1]) * 12 + Number(match[2]) - 1;
};

const getMonthFromIndex = (index) => {
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  return `${year}-${String(month).padStart(2, "0")}`;
};

const getToday = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLastDayOfMonth = (month) => {
  const [year, monthNumber] = String(month || "").split("-").map(Number);
  if (!year || !monthNumber) return 31;
  return new Date(year, monthNumber, 0).getDate();
};

const getMonthDateRange = (month) => {
  const normalizedMonth = String(month || "").slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(normalizedMonth)) return { from: "", to: "" };
  const lastDay = getLastDayOfMonth(normalizedMonth);
  return {
    from: `${normalizedMonth}-01`,
    to: `${normalizedMonth}-${String(lastDay).padStart(2, "0")}`,
  };
};

const getRepaymentDay = (maturityDate) => {
  if (!maturityDate || !/^\d{4}-\d{2}-\d{2}$/.test(maturityDate)) return null;
  const day = Number(maturityDate.slice(-2));
  return Number.isFinite(day) && day > 0 ? day : null;
};

const getRemainingBalanceForFixedPayment = (payment, monthlyRate, months) => {
  if (payment <= 0 || months <= 0) return null;
  if (monthlyRate === 0) return payment * months;
  return (payment * ((1 + monthlyRate) ** months - 1)) /
    (monthlyRate * (1 + monthlyRate) ** months);
};

const resolveExpenseDateForMonth = (month, maturityDate) => {
  const normalizedMonth = String(month || "").slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(normalizedMonth)) return null;
  const repaymentDay = getRepaymentDay(maturityDate);
  if (!repaymentDay) return null;
  const day = Math.min(repaymentDay, getLastDayOfMonth(normalizedMonth));
  return `${normalizedMonth}-${String(day).padStart(2, "0")}`;
};

const getPrincipalPaidForRound = (installment, balanceBeforePayment, round) => {
  const overrideBalance = getScheduleOverrideValue(installment, round, "balance");
  if (overrideBalance !== null) {
    return Math.round(Math.max(0, Math.min(balanceBeforePayment, balanceBeforePayment - overrideBalance)));
  }

  const overridePrincipal = getScheduleOverrideValue(installment, round, "principal");
  if (overridePrincipal !== null) {
    return Math.round(Math.max(0, Math.min(balanceBeforePayment, overridePrincipal)));
  }

  const annualRate = toNumberOrNull(installment.interest_rate) || 0;
  const monthlyRate = annualRate / 100 / 12;
  const amount = getScheduleOverrideValue(installment, round, "payment") ?? toNumberOrNull(installment.amount) ?? 0;
  const firstPaymentAmount = toNumberOrNull(installment.first_payment_amount);
  const isFirstRound = round === 1 && firstPaymentAmount !== null;
  const payment = getScheduleOverrideValue(installment, round, "payment") ?? (isFirstRound ? firstPaymentAmount : amount);
  if (!payment || balanceBeforePayment <= 0) return 0;

  const configuredFirstInterest = toNumberOrNull(installment.first_payment_interest);
  const stampDuty = isFirstRound ? toNumberOrNull(installment.stamp_duty) : null;
  if (isFirstRound && configuredFirstInterest !== null && stampDuty !== null) {
    return Math.round(Math.max(
      0,
      Math.min(balanceBeforePayment, payment - configuredFirstInterest - stampDuty)
    ));
  }

  if (isFirstRound) {
    const maturityRound = Math.floor(toNumberOrNull(installment.maturity_round) || 0);
    const remainingRounds = maturityRound - round;
    const targetBalance = getRemainingBalanceForFixedPayment(amount, monthlyRate, remainingRounds);
    if (targetBalance !== null) {
      return Math.round(Math.max(0, Math.min(balanceBeforePayment, balanceBeforePayment - targetBalance)));
    }
  }

  const interest = balanceBeforePayment * monthlyRate;
  return Math.round(Math.max(0, Math.min(balanceBeforePayment, payment - interest - (stampDuty || 0))));
};

const getProjectedBalanceBeforeMonth = (installment, monthOffset, baseRound) => {
  let balance = toNumberOrNull(installment.balance_before_payment);
  if (balance === null) return "";

  for (let offset = 0; offset < monthOffset; offset += 1) {
    const round = baseRound + offset;
    balance = Math.round(Math.max(0, balance - getPrincipalPaidForRound(installment, balance, round)));
  }

  return Math.round(balance);
};

const projectInstallmentToMonth = (installment, targetMonth) => {
  const baseMonthIndex = getMonthIndex(installment.installment_month);
  const targetMonthIndex = getMonthIndex(toMonthDate(targetMonth));
  const earlyRepaymentMonthIndex = getMonthIndex(installment.early_repaid_date);
  const baseDerived = deriveInstallmentScheduleDates(
    installment.loan_start_date, installment.maturity_date, installment.installment_month
  );
  const baseRound = Math.floor(toNumberOrNull(installment.current_round) ?? baseDerived?.currentRound ?? 0);
  const maturityRound = Math.floor(toNumberOrNull(installment.maturity_round) ?? baseDerived?.termMonths ?? 0);
  if (baseMonthIndex === null || targetMonthIndex === null) return null;
  if (earlyRepaymentMonthIndex !== null && targetMonthIndex >= earlyRepaymentMonthIndex) return null;

  if (baseRound <= 0 || maturityRound <= 0) {
    const targetRound = deriveInstallmentScheduleDates(
      installment.loan_start_date, installment.maturity_date, toMonthDate(targetMonth)
    )?.currentRound ?? 0;
    const canProject = baseRound <= 0 && maturityRound > 0 &&
      targetMonthIndex > baseMonthIndex &&
      targetMonthIndex <= getMonthIndex(installment.maturity_date) && targetRound > 0;
    if (baseMonthIndex !== targetMonthIndex && !canProject) return null;

    let projectedBalance = toNumberOrNull(installment.balance_before_payment);
    if (canProject && projectedBalance !== null) {
      for (let monthIndex = baseMonthIndex; monthIndex < targetMonthIndex; monthIndex += 1) {
        const precedingRound = deriveInstallmentScheduleDates(
          installment.loan_start_date, installment.maturity_date, toMonthDate(getMonthFromIndex(monthIndex))
        )?.currentRound ?? 0;
        if (precedingRound > 0) {
          projectedBalance = Math.round(Math.max(
            0, projectedBalance - getPrincipalPaidForRound(installment, projectedBalance, precedingRound)
          ));
        }
      }
    }
    return {
      ...installment,
      source_id: installment.source_id || installment.id,
      installment_month: toMonthDate(targetMonth),
      current_round: canProject ? targetRound : (installment.current_round ?? baseDerived?.currentRound ?? ""),
      maturity_round: installment.maturity_round ?? baseDerived?.termMonths ?? "",
      balance_before_payment: canProject ? projectedBalance ?? "" : installment.balance_before_payment,
    };
  }

  const monthOffset = targetMonthIndex - baseMonthIndex;
  const projectedRound = baseRound + monthOffset;
  if (projectedRound < baseRound || projectedRound > maturityRound) return null;

  return {
    ...installment,
    source_id: installment.source_id || installment.id,
    installment_month: toMonthDate(targetMonth),
    current_round: projectedRound,
    balance_before_payment: getProjectedBalanceBeforeMonth(installment, monthOffset, baseRound),
  };
};

const getPayableAmount = (installment) => {
  const currentRound = Math.floor(toNumberOrNull(installment.current_round) || 0);
  if (currentRound <= 0) return 0;
  const overridePayment = getScheduleOverrideValue(installment, currentRound, "payment");
  if (overridePayment !== null) return overridePayment;

  const firstPaymentAmount = toNumberOrNull(installment.first_payment_amount);
  if (currentRound === 1 && firstPaymentAmount !== null) return firstPaymentAmount;
  return toNumberOrNull(installment.amount) || 0;
};

const normalizeCarNumber = (value) => String(value || "").replace(/\s+/g, "").trim();

const normalizeText = (value) => String(value || "").trim().replace(/\s+/g, " ");

const getCarLookupKeys = (car) =>
  [
    car.car_number,
    car.car_number_only,
    normalizeCarNumber(car.car_number),
    normalizeCarNumber(car.car_number_only),
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

const buildCarMaps = (cars) => {
  const byId = new Map();
  const byNumber = new Map();
  (cars || []).forEach((car) => {
    if (car.id) byId.set(car.id, car);
    getCarLookupKeys(car).forEach((key) => byNumber.set(key, car));
  });
  return { byId, byNumber };
};

const findCar = (installment, carMaps) => {
  if (installment.car_id && carMaps.byId.has(installment.car_id)) {
    return carMaps.byId.get(installment.car_id);
  }
  const keys = [
    installment.car_number,
    normalizeCarNumber(installment.car_number),
    String(installment.car_number || "").replace(/\D/g, "").slice(-4),
  ].filter(Boolean);
  for (const key of keys) {
    const car = carMaps.byNumber.get(key);
    if (car) return car;
  }
  return null;
};

export const getInstallmentContractKey = (installment) =>
  [
    installment.car_id || normalizeCarNumber(installment.car_number),
    normalizeText(installment.finance_company),
    normalizeText(installment.loan_start_date),
    normalizeText(installment.maturity_date),
    normalizeText(installment.maturity_round),
    normalizeText(installment.interest_rate),
  ].join("__");

const chooseProjectedInstallmentForMonth = (left, right, targetMonthIndex) => {
  if (!left) return right;

  const leftMonthIndex = getMonthIndex(left.source_installment_month || left.installment_month);
  const rightMonthIndex = getMonthIndex(right.source_installment_month || right.installment_month);
  const leftDistance =
    leftMonthIndex === null ? Number.MAX_SAFE_INTEGER : Math.abs(targetMonthIndex - leftMonthIndex);
  const rightDistance =
    rightMonthIndex === null ? Number.MAX_SAFE_INTEGER : Math.abs(targetMonthIndex - rightMonthIndex);

  if (leftDistance !== rightDistance) return rightDistance < leftDistance ? right : left;
  return Number(right.source_id || right.id || 0) > Number(left.source_id || left.id || 0) ? right : left;
};

const projectUniqueInstallmentsToMonth = (installments, month) => {
  const targetMonthIndex = getMonthIndex(toMonthDate(month));
  if (targetMonthIndex === null) return [];

  const grouped = new Map();
  (installments || []).forEach((installment) => {
    const projected = projectInstallmentToMonth(installment, month);
    if (!projected) return;

    const contractKey = getInstallmentContractKey(installment);
    const current = grouped.get(contractKey);
    grouped.set(contractKey, chooseProjectedInstallmentForMonth(current, projected, targetMonthIndex));
  });

  return Array.from(grouped.values());
};

const buildExpenseMemo = (sourceId) => `${INSTALLMENT_EXPENSE_MEMO_PREFIX} #${sourceId}`;

const getExpenseKey = (row) => [row.date || "", row.bigo || ""].join("__");

const getExpenseComparableKey = (row) =>
  [
    row.date || "",
    normalizeCarNumber(row.car_number),
    normalizeText(row.human_name),
    Number(row.halbu || 0),
  ].join("__");

const makeZeroExpenseFields = () => ({
  johab: 0,
  unyoung: 0,
  gps: 0,
  halbu: 0,
  geubyeo: 0,
  "4bohum": 0,
  tonghaeng: 0,
  joocha: 0,
  sooksic: 0,
  jungbi: 0,
  jooyu: 0,
  bohum: 0,
  secha: 0,
  chooljang: 0,
  gwatae: 0,
  gita: 0,
});

const fetchCompanyCars = async (supabase, company) => {
  const { data, error } = await supabase
    .from("cars")
    .select("id, car_number, car_number_only, driver, phone, company_name")
    .eq("company", company)
    .range(0, 4999);

  if (error) throw error;
  return data || [];
};

const syncExpensePayloads = async (
  supabase,
  expensePayloads,
  {
    retriedAfterConflict = false,
    company: cleanupCompany = "",
    fromDate = "",
    toDate = "",
    cleanupObsolete = false,
    cleanupMemos = [],
  } = {}
) => {
  const payloadByKey = new Map();
  expensePayloads.filter(Boolean).forEach((payload) => {
    payloadByKey.set(getExpenseKey(payload), payload);
  });
  const payloads = Array.from(payloadByKey.values());
  const company = payloads[0]?.company || cleanupCompany;
  const dates = payloads.map((row) => row.date).filter(Boolean).sort();
  const rangeFrom = dates[0] || fromDate;
  const rangeTo = dates[dates.length - 1] || toDate;

  if (!company || !rangeFrom || !rangeTo) return { inserted: 0, updated: 0, deleted: 0 };

  const memos = Array.from(new Set(payloads.map((row) => row.bigo).filter(Boolean)));
  const { data: existingRows, error: findError } = await supabase
    .from("cars-expenses")
    .select("id, date, car_number, human_name, halbu, bigo")
    .eq("company", company)
    .gte("date", rangeFrom)
    .lte("date", rangeTo)
    .ilike("bigo", `${INSTALLMENT_EXPENSE_MEMO_PREFIX} #%`)
    .range(0, 9999);

  if (findError) throw findError;

  const existingByKey = new Map((existingRows || []).map((row) => [getExpenseKey(row), row]));
  const payloadMemoSet = new Set(memos);
  const cleanupMemoSet = new Set(cleanupMemos.filter(Boolean));
  const payloadComparableKeys = new Set(payloads.map(getExpenseComparableKey));
  const staleDuplicateIds = (existingRows || [])
    .filter(
      (row) =>
        !payloadMemoSet.has(row.bigo) &&
        payloadComparableKeys.has(getExpenseComparableKey(row))
    )
    .map((row) => row.id)
    .filter(Boolean);
  const obsoleteIds = cleanupObsolete
    ? (existingRows || [])
        .filter((row) => !payloadMemoSet.has(row.bigo))
        .map((row) => row.id)
        .filter(Boolean)
    : [];
  const targetedObsoleteIds = cleanupMemoSet.size > 0
    ? (existingRows || [])
        .filter((row) => cleanupMemoSet.has(row.bigo) && !payloadMemoSet.has(row.bigo))
        .map((row) => row.id)
        .filter(Boolean)
    : [];
  const deleteIds = Array.from(new Set([...staleDuplicateIds, ...obsoleteIds, ...targetedObsoleteIds]));
  const updates = [];
  const inserts = [];

  payloads.forEach((payload) => {
    const existing = existingByKey.get(getExpenseKey(payload));
    if (existing?.id) {
      updates.push({ id: existing.id, ...payload });
    } else {
      inserts.push(payload);
    }
  });

  if (updates.length > 0) {
    const { error } = await supabase.from("cars-expenses").upsert(updates, { onConflict: "id" });
    if (error) throw error;
  }

  if (inserts.length > 0) {
    const { error } = await supabase.from("cars-expenses").insert(inserts);
    if (error) {
      if (error.code === "23505" && !retriedAfterConflict) {
        return syncExpensePayloads(supabase, payloads, {
          retriedAfterConflict: true,
          company,
          fromDate: rangeFrom,
          toDate: rangeTo,
          cleanupObsolete,
        });
      }
      throw error;
    }
  }

  if (deleteIds.length > 0) {
    const { error } = await supabase
      .from("cars-expenses")
      .delete()
      .eq("company", company)
      .in("id", deleteIds);
    if (error) throw error;
  }

  return { inserted: inserts.length, updated: updates.length, deleted: deleteIds.length };
};

export const buildInstallmentExpensePayloads = ({ installments, month, company, companyName, cars }) => {
  const carMaps = buildCarMaps(cars);
  return projectUniqueInstallmentsToMonth(installments, month)
    .map((projected) => {
      const installment = projected;
      const sourceId = installment.source_id || installment.id;
      if (!sourceId) return null;

      const amount = getPayableAmount(projected);
      const expenseDate = resolveExpenseDateForMonth(month, projected.maturity_date);
      if (!expenseDate || amount <= 0) return null;

      const car = findCar(projected, carMaps);
      const carNumber = String(car?.car_number_only || projected.car_number || "").trim();
      if (!carNumber) return null;

      return {
        company,
        company_name: car?.company_name || companyName || null,
        date: expenseDate,
        car_number: carNumber,
        human_name: car?.driver || "",
        phone: car?.phone || null,
        ...makeZeroExpenseFields(),
        halbu: amount,
        bigo: buildExpenseMemo(sourceId),
      };
    })
    .filter(Boolean);
};

export const syncInstallmentExpensesForRows = async ({
  supabase,
  installments,
  month,
  company,
  companyName,
}) => {
  if (!company || !month || !installments?.length) return { inserted: 0, updated: 0 };
  const cars = await fetchCompanyCars(supabase, company);
  const payloads = buildInstallmentExpensePayloads({
    installments,
    month,
    company,
    companyName,
    cars,
  });
  const { from, to } = getMonthDateRange(month);
  const cleanupMemos = (installments || [])
    .map((installment) => installment?.source_id || installment?.id)
    .filter(Boolean)
    .map(buildExpenseMemo);

  return syncExpensePayloads(supabase, payloads, {
    company,
    fromDate: from,
    toDate: to,
    cleanupMemos,
  });
};

export const ensureDueInstallmentExpenses = async ({ supabase, dateRange, company, companyName }) => {
  if (!company || !dateRange?.from || !dateRange?.to) return { inserted: 0, updated: 0 };

  const today = getToday();
  const from = dateRange.from;
  const to = dateRange.to > today ? today : dateRange.to;
  if (to < from) return { inserted: 0, updated: 0 };

  const fromMonthIndex = getMonthIndex(from);
  const toMonthIndex = getMonthIndex(to);
  if (fromMonthIndex === null || toMonthIndex === null) return { inserted: 0, updated: 0 };

  const [{ data: installments, error: installmentsError }, cars] = await Promise.all([
    supabase
      .from("car_installments")
      .select("*")
      .eq("company", company)
      .range(0, 4999),
    fetchCompanyCars(supabase, company),
  ]);

  if (installmentsError) throw installmentsError;

  const allPayloads = [];
  for (let monthIndex = fromMonthIndex; monthIndex <= toMonthIndex; monthIndex += 1) {
    const targetMonth = getMonthFromIndex(monthIndex);
    const payloads = buildInstallmentExpensePayloads({
      installments: installments || [],
      month: targetMonth,
      company,
      companyName,
      cars,
    }).filter((payload) => payload.date >= from && payload.date <= to);
    allPayloads.push(...payloads);
  }

  return syncExpensePayloads(supabase, allPayloads, {
    company,
    fromDate: from,
    toDate: to,
    cleanupObsolete: true,
  });
};
